import { useState, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { Input, Button } from '@predoc/cow';
import { useDebounce } from '@/hooks/useDebounce';
import { useSearchProviders } from '@/feature/patients/hooks/providerAPI';
import { ProviderType, ProviderSearchResult } from '@/feature/patients/types';

interface FacilityDropdownProps {
  value?: string; // Now takes facilityName string instead of full object
  onChange: (facility: ProviderSearchResult | undefined) => void;
  disabled?: boolean;
  className?: string;
  onInputChange?: (value: string) => void;
}

export const FacilityDropdown = ({
  value = '',
  onChange,
  disabled = false,
  className = 'w-full',
  onInputChange,
}: FacilityDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const debouncedSearchValue = useDebounce(searchInput, 300);

  // Only search when user types 3+ characters to avoid excessive API calls
  const searchParams = useMemo(() => {
    if (
      !debouncedSearchValue?.trim() ||
      debouncedSearchValue.trim().length < 3
    ) {
      return undefined;
    }

    return {
      providerType: ProviderType.FACILITY,
      facilityName: debouncedSearchValue.trim(),
      pagination: {
        limit: 20,
        offset: 0,
      },
    };
  }, [debouncedSearchValue]);

  const { data: searchResults, isLoading } = useSearchProviders(searchParams);

  const facilities = useMemo(() => {
    return Array.isArray(searchResults)
      ? searchResults
      : searchResults?.data || [];
  }, [searchResults]);

  const handleSelect = (facility: ProviderSearchResult) => {
    onChange(facility);
    setSearchInput('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange(undefined);
    onInputChange?.('');
    setSearchInput('');
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchInput(newValue);

    // Always update the form value with what user types (supports manual entry)
    onInputChange?.(newValue);

    if (newValue.trim()) {
      setOpen(true);
    }
  };

  // value (from form) is the single source of truth for display;
  // searchQuery is only used internally for debounced API search

  return (
    <div
      className={`relative ${className}`}
      onBlur={(e) => {
        // Close only if focus moved outside this container
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setOpen(false);
        }
      }}
    >
      <div className="relative">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={value}
          onChange={handleInputChange}
          placeholder="Search for a facility..."
          className={`pl-10 ${value ? 'pr-16' : 'pr-4'}`} // space for clear button if value is present
          disabled={disabled}
          onFocus={() => setOpen(true)}
        />

        {value && (
          <Button
            variant="ghost"
            type="button"
            onClick={handleClear}
            className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Clear selection"
          >
            <X className="size-4" />
          </Button>
        )}
      </div>

      {open ? (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="max-h-60 overflow-auto">
            {isLoading ? (
              <div className="flex items-center justify-center p-6">
                <div className="text-sm text-gray-600">Searching...</div>
              </div>
            ) : facilities.length === 0 && debouncedSearchValue ? (
              <div className="p-6 text-center">
                <p className="text-sm text-gray-600">
                  No facilities found for &ldquo;{debouncedSearchValue}&rdquo;
                </p>
              </div>
            ) : facilities.length === 0 &&
              (!debouncedSearchValue || debouncedSearchValue.length < 3) ? (
              <div className="p-6 text-center">
                <Search className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                <p className="text-sm text-gray-600">
                  Type at least 3 characters to search for facilities
                </p>
              </div>
            ) : (
              <div className="space-y-1 p-1">
                {facilities.map(
                  (facility: ProviderSearchResult, index: number) => {
                    const npi =
                      facility.facilityNpi ||
                      facility.providerNpi ||
                      `facility-${index}`;
                    return (
                      <Button
                        key={npi}
                        variant="ghost"
                        className="h-auto w-full justify-start rounded-md p-3 text-left"
                        onClick={() => handleSelect(facility)}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {facility.facilityName || 'Unknown Facility'}
                          </div>
                          {facility.specialties &&
                            facility.specialties.length > 0 && (
                              <div className="mt-1 text-xs text-gray-600">
                                {facility.specialties.slice(0, 2).join(', ')}
                                {facility.specialties.length > 2 && '...'}
                              </div>
                            )}
                        </div>
                      </Button>
                    );
                  },
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

FacilityDropdown.displayName = 'FacilityDropdown';
